import type { IContest } from '../types/global/helper';

const toNonNegativeInt = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0) {
        return Math.floor(value);
    }
    if (typeof value === 'string' && value.trim() !== '') {
        const n = Number(value);
        if (Number.isFinite(n) && n >= 0) return Math.floor(n);
    }
    return undefined;
};

/** Prefer plural field name `hide_submissions_count` (not hide_submission_count). */
export function isContestSubmissionsCountHidden(contest: IContest | null | undefined): boolean {
    return contest?.hide_submissions_count === true;
}

/** Current application / submission count for display and cap checks. */
export function getContestApplicationCount(contest: IContest | null | undefined): number {
    if (!contest) return 0;
    if (isContestSubmissionsCountHidden(contest)) return 0;

    // `submissions` on public contest detail is often a preview carousel, not the full application list.
    const embedded = contest.submissions?.length ?? 0;
    const candidates = [
        toNonNegativeInt(contest.applications),
        toNonNegativeInt(contest.submissions_total_count),
        toNonNegativeInt((contest as { total_submissions?: unknown }).total_submissions),
        embedded > 0 ? embedded : undefined,
    ].filter((n): n is number => n !== undefined);

    return candidates.length > 0 ? Math.max(...candidates) : 0;
}

/** Social proof line for contest / campaign discovery cards. Empty when count is 0 (hide “Be the first…”). */
export function getContestApplicationCountLabel(count: number): string {
    if (count <= 0) return '';
    if (count === 1) return '1 person has applied';
    return `${count} people have applied`;
}

/** Positive cap when set; null means unlimited. */
export function getContestMaxParticipants(contest: IContest | null | undefined): number | null {
    if (!contest) return null;
    const raw = contest.max_participants ?? (contest as { max_capacity?: number }).max_capacity;
    if (raw == null) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.floor(n);
}

export function isContestParticipantCapReached(contest: IContest | null | undefined): boolean {
    // Public payloads omit applications when hidden — trust can_apply in submission phase.
    if (isContestSubmissionsCountHidden(contest) && typeof contest?.can_apply === 'boolean') {
        return contest.contest_phase === 'submission' && contest.can_apply === false;
    }
    const max = getContestMaxParticipants(contest);
    if (max == null) return false;
    return getContestApplicationCount(contest) >= max;
}

export function getContestCapacityPercent(contest: IContest | null | undefined): number {
    const max = getContestMaxParticipants(contest);
    if (max == null) return 0;
    const count = getContestApplicationCount(contest);
    return Math.min(100, Math.round((count / max) * 100));
}
